import { useState } from 'react';
import Layout from '@/components/feature/Layout';
import { usePageMeta } from '@/hooks/usePageMeta';

const FORM_ENDPOINT = 'https://readdy.ai/api/form/db78iuv799gd4v2fed4g';

const stats = [
  { icon: 'ri-film-line', value: '60+', label: '示范视频内容' },
  { icon: 'ri-apps-2-line', value: '3', label: '主要分类' },
  { icon: 'ri-user-smile-line', value: '10万+', label: '每日浏览用户' },
  { icon: 'ri-global-line', value: '12', label: '覆盖地区' },
];

const values = [
  {
    icon: 'ri-stack-line',
    title: '内容为王',
    desc: '汇聚电影、短视频、娱乐等多类型优质内容，让每一次打开都能发现新的精彩。',
  },
  {
    icon: 'ri-flashlight-line',
    title: '快速发现',
    desc: '通过分类筛选、关键词搜索与智能推荐，帮助你在海量内容中快速定位兴趣所在。',
  },
  {
    icon: 'ri-heart-3-line',
    title: '体验优先',
    desc: '简洁明快的界面与流畅的浏览节奏，只为让你更舒适地享受每一次探索。',
  },
];

const faqs = [
  {
    q: '涩涩视频的内容都是免费的吗？',
    a: '本站为视频内容聚合演示平台，所有内容均用于展示聚合、筛选与搜索体验，暂不提供在线播放服务。',
  },
  {
    q: '为什么点击封面没有开始播放？',
    a: '当前暂无播放接口支持，视频相关功能仅展示封面与资料信息，不包含播放器及实际播放控制。',
  },
  {
    q: '搜索结果和真实的多吗？',
    a: '搜索与筛选均为前端模拟实现，用于演示匹配交互效果，实际内容以页面展示的示范数据为准。',
  },
  {
    q: '如何反馈问题或提出建议？',
    a: '你可以通过下方留言表单或示例邮箱与我们联系，我们非常期待听到你的想法。',
  },
];

export default function AboutPage() {
  usePageMeta(
    '关于我们 - 涩涩视频热门视频聚合平台',
    '了解涩涩视频的网站定位、使命与联系方式。我们致力于聚合丰富多样的视频内容，带来流畅舒适的浏览与探索体验。',
  );

  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    // 蜜罐校验
    const honeypot = String(formData.get('website_alt') ?? '').trim();
    if (honeypot) {
      setStatus('success');
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === 'website_alt') return;
      const str = typeof value === 'string' ? value : '';
      if (str.trim() === '') return;
      payload.append(key, str);
    });

    setStatus('submitting');
    setFormError('');
    try {
      const response = await fetch(FORM_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = responseText ? JSON.parse(responseText) : null;
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.meta?.detail || parsed?.message || responseText || '';
      const isSpam = String(serverMsg).toLowerCase().includes('spam');

      if (response.ok && parsed?.code === 'OK' && !isSpam) {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
        setFormError(serverMsg || '提交失败，请稍后重试。');
      }
    } catch {
      setStatus('error');
      setFormError('网络异常，提交失败，请稍后重试。');
    }
  };

  return (
    <Layout>
      {/* Intro band */}
      <section className="border-b border-background-200/70 bg-background-200/50">
        <div className="mx-auto w-full px-4 py-12 text-center md:px-8 md:py-16">
          <span className="inline-flex items-center gap-2 rounded-full bg-primary-100 px-4 py-1.5 text-xs font-medium text-primary-700">
            <i className="ri-play-circle-fill" />
            关于涩涩视频
          </span>
          <h1 className="mt-5 font-heading text-3xl font-extrabold text-foreground-950 md:text-4xl">
            让每一个精彩视频，都能被轻松发现
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            涩涩视频是一个热门视频聚合平台，覆盖电影、短视频、娱乐等多种类型，
            为广泛追求丰富多样视频内容的观众提供便捷的浏览、筛选与内容发现服务。
          </p>
        </div>
      </section>

      {/* Stats */}
      <section className="mx-auto w-full px-4 py-10 md:px-8">
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          {stats.map((s) => (
            <div
              key={s.label}
              className="flex flex-col items-center rounded-lg border border-background-200/70 bg-background-50 py-6"
            >
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-accent-100 text-accent-600">
                <i className={`${s.icon} text-2xl`} />
              </span>
              <span className="mt-3 font-heading text-2xl font-extrabold text-foreground-950">{s.value}</span>
              <span className="mt-1 text-xs text-foreground-500">{s.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Intro text */}
      <section className="mx-auto w-full px-4 pb-4 md:px-8">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
              <span className="h-5 w-1.5 rounded-full bg-primary-500" />
              网站简介
            </h2>
            <div className="mt-4 space-y-4 text-sm leading-relaxed text-foreground-700">
              <p>
                涩涩视频致力于打造一个内容丰富、使用便捷的视频聚合平台。我们希望打破内容分散的困扰，
                把电影、短视频、娱乐节目等多类型内容汇集到同一个清爽的界面中，让你无需在多个站点之间来回切换。
              </p>
              <p>
                通过清晰的分类导航、灵活的筛选工具与直观的搜索功能，你可以按照类型、地区、年份与热度快速缩小范围，
                找到真正想看的内容。每一个视频都有自己的详情页面，包含完整的资料与相关推荐，
                帮助你从一条内容自然延伸到更多精彩内容。
              </p>
              <p>
                需要说明的是，本站目前为内容聚合演示站点，视频播放相关功能仅提供封面与资料展示，
                搜索与筛选为前端模拟实现，用于演示完整的浏览与探索体验。
              </p>
            </div>
          </div>

          <div className="rounded-lg border border-background-200/70 bg-background-50 p-5">
            <h2 className="font-heading text-base font-bold text-foreground-950">联系我们</h2>
            <ul className="mt-4 space-y-3 text-sm text-foreground-600">
              <li className="flex items-center gap-2">
                <i className="ri-mail-line text-primary-500" />
                合作邮箱：hello@seseshipin.demo
              </li>
              <li className="flex items-center gap-2">
                <i className="ri-customer-service-2-line text-primary-500" />
                用户反馈：feedback@seseshipin.demo
              </li>
              <li className="flex items-center gap-2">
                <i className="ri-time-line text-primary-500" />
                服务时间：周一至周五 10:00 - 19:00
              </li>
            </ul>
            <p className="mt-4 rounded-md bg-background-100 p-3 text-xs leading-relaxed text-foreground-500">
              我们欢迎内容合作、意见反馈与任何形式的建议，期待与热爱视频的每一位朋友同行。
            </p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="mx-auto w-full px-4 py-10 md:px-8">
        <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
          <span className="h-5 w-1.5 rounded-full bg-accent-500" />
          我们的坚持
        </h2>
        <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-3">
          {values.map((v) => (
            <div
              key={v.title}
              className="rounded-lg border border-background-200/70 bg-background-50 p-5 transition-all hover:-translate-y-1 hover:border-primary-300"
            >
              <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-100 text-primary-600">
                <i className={`${v.icon} text-xl`} />
              </span>
              <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">{v.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">{v.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="mx-auto w-full px-4 pb-10 md:px-8">
        <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
          <span className="h-5 w-1.5 rounded-full bg-primary-500" />
          常见问题
        </h2>
        <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
          {faqs.map((f) => (
            <div key={f.q} className="rounded-lg border border-background-200/70 bg-background-50 p-5">
              <h3 className="flex items-start gap-2 text-sm font-semibold text-foreground-950">
                <i className="ri-question-line mt-0.5 text-primary-500" />
                {f.q}
              </h3>
              <p className="mt-2 pl-6 text-sm leading-relaxed text-foreground-600">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Contact form */}
      <section className="mx-auto w-full px-4 pb-14 md:px-8">
        <div className="rounded-lg border border-background-200/70 bg-background-200/50 p-6 md:p-8">
          <div className="max-w-2xl">
            <h2 className="font-heading text-xl font-bold text-foreground-950">给我们留言</h2>
            <p className="mt-2 text-sm text-foreground-600">
              无论是内容建议、合作意向还是使用体验反馈，欢迎留下你的想法，我们会认真阅读每一条留言。
            </p>
          </div>

          <form
            id="about-contact-form"
            data-readdy-form
            onSubmit={handleSubmit}
            className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2"
          >
            <div className="flex flex-col gap-1.5">
              <label htmlFor="contact-name" className="text-xs font-medium text-foreground-600">
                称呼
              </label>
              <input
                id="contact-name"
                name="name"
                type="text"
                required
                placeholder="请输入你的称呼"
                className="rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="contact-email" className="text-xs font-medium text-foreground-600">
                邮箱
              </label>
              <input
                id="contact-email"
                name="email"
                type="email"
                required
                placeholder="请输入你的邮箱"
                className="rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
              />
            </div>
            <div className="flex flex-col gap-1.5 md:col-span-2">
              <label htmlFor="contact-subject" className="text-xs font-medium text-foreground-600">
                留言类型
              </label>
              <select
                id="contact-subject"
                name="subject"
                defaultValue="内容建议"
                className="cursor-pointer rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
              >
                <option value="内容建议">内容建议</option>
                <option value="合作意向">合作意向</option>
                <option value="问题反馈">问题反馈</option>
                <option value="其他">其他</option>
              </select>
            </div>
            <div className="flex flex-col gap-1.5 md:col-span-2">
              <label htmlFor="contact-message" className="text-xs font-medium text-foreground-600">
                留言内容
              </label>
              <textarea
                id="contact-message"
                name="message"
                required
                maxLength={500}
                rows={5}
                placeholder="请填写你的留言内容（最多 500 字）"
                className="resize-none rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
              />
            </div>

            {/* 反垃圾蜜罐字段 */}
            <div className="hp-field-wrap" aria-hidden="true">
              <label htmlFor="contact-website-alt">Website</label>
              <input
                id="contact-website-alt"
                name="website_alt"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                readOnly
              />
            </div>

            <div className="flex flex-col gap-3 md:col-span-2">
              <div className="flex flex-wrap items-center gap-4">
                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <i className="ri-send-plane-line" />
                  {status === 'submitting' ? '提交中…' : '提交留言'}
                </button>
                {status === 'success' && (
                  <span className="flex items-center gap-1.5 text-sm font-medium text-primary-600">
                    <i className="ri-checkbox-circle-line" />
                    提交成功，感谢你的留言！
                  </span>
                )}
                {status === 'error' && (
                  <span className="flex items-center gap-1.5 text-sm font-medium text-accent-600">
                    <i className="ri-error-warning-line" />
                    {formError}
                  </span>
                )}
              </div>
            </div>
          </form>
        </div>
      </section>
    </Layout>
  );
}